/**
 * What a logo is, structurally — the single owner of that answer.
 *
 * Pure DOM. No `store`, no `bridge`, no events, so it works on a parsed
 * document as readily as on the live one, which is what lets a batch operation
 * (brand-kit import) reuse the editor's own notion of a logo instead of
 * inventing a second one.
 *
 * WHY THIS FILE EXISTS. Before it, the same two selector strings were written
 * out in three modules and the predicate in two — `logo-editor.js`,
 * `editable-utils.js` and `logo-normalizer.js` each carried a copy. That is the
 * shape of bug where someone widens the selector in one place, everything keeps
 * working, and a year later a template's nav logo is editable but not
 * replaceable for no discoverable reason.
 *
 * THE TWO PREDICATES ARE NOT THE SAME, AND THAT IS DELIBERATE. They were
 * subtly different before this consolidation, and collapsing them into one
 * would silently change behaviour in whichever caller lost:
 *
 *   · `isLogoWrapper` (the editor's) also accepts an element already stamped
 *     `data-pb-editable="logo"`. It has to: by the time the editor asks, the
 *     stamp is the authority, and `parseLogoHTML` only injects attrs when the
 *     eid lookup misses.
 *   · `isLogoEditableWrapper` (the stamper's) additionally requires the slot to
 *     CONTAIN something (`img`/`svg`/`picture`). It has to: stamping an empty
 *     slot as editable offers the user a logo that is not there.
 *
 * So both live here, named for what they mean, and the difference is a comment
 * rather than an accident.
 */

/**
 * Anchors a template may use for its logo. Extend here and nowhere else.
 *
 * The array is the source and the selector string is derived from it, so the
 * two forms cannot drift — they were separately hand-written before this file
 * existed, which is one edit away from a normalizer and an editor disagreeing
 * about what a logo is.
 */
export const LOGO_ANCHOR_SELECTORS = Object.freeze([
  'a.logo',
  'a.cs-logo',
  'a.navbar-brand',
  'a.brand',
  'a.footer-logo'
]);
export const LOGO_WRAPPER_SELECTOR = LOGO_ANCHOR_SELECTORS.join(', ');

/** What can sit inside a `.logo-slot` and be the mark itself. */
export const LOGO_INNER_SELECTOR = 'img, svg, picture';

const SITE_ROOT_HREF = /^(?:\/|\.\/|index\.html|\/index\.html|#)$/i;
const LOGO_REGION_SELECTOR = 'header, nav, footer, [role="banner"], [role="contentinfo"]';

/** Narrow structural fallback for classless nav/header/footer brand links. */
export function isStructuralLogoCandidate(el) {
  if (!el?.matches?.('a')) return false;
  if (!SITE_ROOT_HREF.test(String(el.getAttribute('href') || '').trim())) return false;
  if (!el.closest(LOGO_REGION_SELECTOR)) return false;
  if (el.closest('ul, ol, [role="menubar"]')) return false;
  const marks = Array.from(el.querySelectorAll(LOGO_INNER_SELECTOR)).filter(
    (mark) => !mark.parentElement?.closest?.(LOGO_INNER_SELECTOR)
  );
  if (marks.length !== 1) return false;
  const directText = Array.from(el.childNodes || [])
    .filter((node) => node.nodeType === 3)
    .map((node) => node.textContent || '')
    .join(' ')
    .trim();
  return !directText || directText.split(/\s+/).length <= 4;
}

/**
 * The editor's notion: a stamped logo, or a known anchor that has been
 * normalized (i.e. carries a `.logo-slot`).
 */
export function isLogoWrapper(el) {
  return (
    !!(el && el.getAttribute?.('data-pb-editable') === 'logo') ||
    !!(el?.matches?.(LOGO_WRAPPER_SELECTOR) && el.querySelector?.('.logo-slot'))
  );
}

/**
 * The stamper's notion: a known anchor whose slot actually holds a mark.
 * Stricter than `isLogoWrapper` on purpose — see the header.
 */
export function isLogoEditableWrapper(el) {
  if (!el?.matches?.(LOGO_WRAPPER_SELECTOR)) return false;
  const slot = el.querySelector('.logo-slot');
  return !!slot && !!slot.querySelector(LOGO_INNER_SELECTOR);
}

/**
 * Every normalized logo wrapper under `root`, including `root` itself.
 *
 * Keyed on the STRUCTURE rather than on `[data-pb-editable="logo"]`, because
 * that attribute is injected lazily and a sweep keyed on it silently finds
 * nothing on the common path.
 */
export function findLogoWrappers(root) {
  if (!root?.querySelectorAll) return [];
  const wrappers = [];
  if (root.matches?.(LOGO_WRAPPER_SELECTOR) && root.querySelector('.logo-slot')) {
    wrappers.push(root);
  }
  for (const wrapper of root.querySelectorAll(LOGO_WRAPPER_SELECTOR)) {
    if (wrapper.querySelector('.logo-slot')) wrappers.push(wrapper);
  }
  return wrappers;
}

/**
 * A replacement's true proportions, as an `aspect-ratio` value.
 *
 * Load-bearing: the slot otherwise keeps the shape of the logo it replaced, so
 * every new mark is letterboxed into a box built for the old one. Measured
 * across ten template headers, painted fill fell from a mean of 80% to 41% for
 * a wide mark and 17% for a square one.
 */
export function aspectRatioFromDimensions(width, height) {
  if (!Number.isFinite(width) || !Number.isFinite(height)) return '';
  if (width <= 0 || height <= 0) return '';
  return `${parseFloat(width.toFixed(4))} / ${parseFloat(height.toFixed(4))}`;
}

/** Stamp the ratio onto the wrapper's slot. A falsy ratio is a no-op. */
export function applyLogoAspectRatio(wrapper, aspectRatio) {
  if (!aspectRatio) return;
  const slot = wrapper?.querySelector?.('.logo-slot');
  if (slot) slot.style.setProperty('--pb-logo-aspect-ratio', aspectRatio);
}

/** The mark elements inside a wrapper's slot. */
export function logoSlotImages(wrapper) {
  const slot = wrapper?.querySelector?.('.logo-slot');
  return slot ? Array.from(slot.querySelectorAll('img')) : [];
}

/**
 * The `<img>` elements a replacement of `variant` should land on.
 *
 * Moved verbatim from `logo-editor.js` (LN-4b) so a batch brand apply and a
 * manual replace resolve targets identically. The index fallback is the
 * template convention `readLogoInfo` documents: the first image is the light
 * mark and the second the dark one, when no class says otherwise.
 */
export function getLogoImageTargets(wrapper, variant = 'all') {
  const images = Array.from(wrapper?.querySelectorAll?.('.logo-slot img') || []);
  if (variant === 'all') return images;

  const classMatches = images.filter((img) => img.classList.contains(variant));
  if (classMatches.length) return classMatches;
  if (variant === 'light' && images[0]) return [images[0]];
  if (variant === 'dark' && images[1]) return [images[1]];
  return images.length === 1 ? [images[0]] : [];
}

/** As above, but materializes an `<img>` when the slot holds something else. */
export function ensureLogoImageTargets(wrapper, variant = 'all') {
  const existing = getLogoImageTargets(wrapper, variant);
  if (existing.length) return existing;
  const slot = wrapper?.querySelector?.('.logo-slot');
  if (!slot) return [];
  const doc = wrapper.ownerDocument || globalThis.document;
  if (!doc?.createElement) return [];
  const oldGraphic = slot.querySelector('svg, picture, img');
  const img = doc.createElement('img');
  img.setAttribute('decoding', 'async');
  const oldClass = String(oldGraphic?.getAttribute?.('class') || '').trim();
  if (oldClass) img.setAttribute('class', oldClass);
  if (variant && variant !== 'all') img.classList.add(variant);
  for (const attr of ['aria-hidden', 'role']) {
    const value = oldGraphic?.getAttribute?.(attr);
    if (value) img.setAttribute(attr, value);
  }
  slot.textContent = '';
  slot.appendChild(img);
  return [img];
}

/** Write one mark's source attributes, including any `<picture>` siblings. */
export function applyImageAttrs(img, { src, alt, assetId, mimeType, width, height }) {
  if (!img) return;
  if (typeof src === 'string' && src) img.setAttribute('src', src);
  if (typeof alt === 'string') img.setAttribute('alt', alt);
  if (assetId) img.setAttribute('data-pb-asset', assetId);
  else img.removeAttribute('data-pb-asset');
  if (mimeType) img.setAttribute('data-pb-mime', mimeType);
  else img.removeAttribute('data-pb-mime');
  if (Number.isFinite(width) && width > 0) img.setAttribute('width', String(Math.round(width)));
  if (Number.isFinite(height) && height > 0) img.setAttribute('height', String(Math.round(height)));

  const picture = img.closest?.('picture');
  if (!picture) return;
  for (const source of picture.querySelectorAll('source')) {
    if (typeof src === 'string' && src) source.setAttribute('srcset', src);
    if (mimeType) source.setAttribute('type', mimeType);
    else source.removeAttribute('type');
  }
}

/**
 * Point one wrapper's marks at a new source — the BATCH-safe form.
 *
 * Deliberately NOT `applyLogoSource` from `logo-editor.js`, whose per-target
 * loop ends with `removeAttribute('data-pb-logo-source-locked')`. That is right
 * when a user deliberately replaces one logo they can see, and wrong for a
 * brand-kit apply, which SKIPS locked wrappers and would otherwise unlock every
 * hand-authored inline-SVG mark it had just promised to leave alone. The
 * attribute is never touched here.
 *
 * @returns {number} how many `<img>` elements were written
 */
export function setWrapperLogoSource(
  wrapper,
  { variant = 'all', aspectRatio = '', ...attrs } = {}
) {
  const images = ensureLogoImageTargets(wrapper, variant);
  for (const img of images) applyImageAttrs(img, attrs);
  if (images.length) applyLogoAspectRatio(wrapper, aspectRatio);
  return images.length;
}

/**
 * Is this wrapper's mark a hand-authored inline SVG element?
 *
 * Callers that replace logos in BULK must skip these by default: swapping an
 * authored vector mark for a raster image element is destructive and
 * irreversible from the user's point of view, and `data-pb-logo-source-locked`
 * is how the editor already records that state.
 */
export function isSourceLockedLogo(wrapper) {
  if (wrapper?.getAttribute?.('data-pb-logo-source-locked') === '1') return true;
  const slot = wrapper?.querySelector?.('.logo-slot');
  return !!slot?.querySelector('svg');
}
